/* eslint-disable no-shadow */
import {Visibility, ViewMode} from '../api';
import {BusMessage} from './ParentMessageBus';

// Valid message types for things communicated to the parent frame.
//
// Note that the string values are matched in our external services so changing
// them will break our sites. You must take a phased approach to migrating to
// new values by first introducing a new value and updating all sites, and once
// that's fully deployed you can remove the other value.
export enum MessageToParentType {
  // Requests the parent to change the visibility of the widget.
  //
  // Because the parent owns the iframe, it has to be the authority on the
  // visibility state. Hence this is merely a request and we expect to receive a
  // message in return if the visibility is actually changed in the parent.
  RequestVisibilityChange = 'request-visibility-change',

  // Updates the parent with the preferred size for the widget.
  //
  // This message allows the widget itself to provide the preferred size for the
  // frame, allowing control for both static sizing as well as enabling the
  // ability to dynamically resize the frame if that's something we want.
  // Putting this control in the widget means we can update sizes in the client
  // and know that all frames on all surfaces will show the appropriate height.
  SetPreferredSize = 'set-preferred-size',

  // Requests the client to update the current view mode, which effects how the iframe is styled by the parent page
  RequestViewMode = 'request-view-mode',

  // Updates the parent with the height of the header.
  //
  // This allows the parent frame to know the proper height of the header for
  // minimizing, since the parent has to align the frame to the bottom of the
  // viewport.
  SetHeaderHeight = 'set-header-height',

  // Requests the latest analytics event ID from the parent.
  //
  // We use the analytics event ID when creating cases so that we can link cases
  // to user sessions in analytics. This is used to calculate, among other
  // things, self serve escalation rates or SSER, a critical metric for self
  // serve support.
  RequestAnalyticsEventId = 'request-analytics-event-id',

  // Updates the parent with the status of unseen messages.
  //
  // This allows the parent frame to know how to change its UI, such as by
  // changing its favicon to show that there are new notifications.
  SetUnseenMessagesCount = 'set-unseen-messages-count',

  // Updates the parent that the widget iFrame is initialized
  //
  // This allows the parent frame when it's okay to open the widget
  // so that the widget isn't opened prematurely (such that it shows blank grey screen)
  SetWidgetInitialized = 'widget-initialized',

  // Requests an action to be performed by the parent/host, e.g. opening a modal
  RequestAction = 'request-action',
}

// Valid message types for things communicated from the parent frame.
export enum MessageFromParentType {
  // An update the widget's visibility has changed.
  //
  // Because the parent owns the iframe, it has to be the authority on the
  // visibility state. This is the message sent to our iframe to let it know
  // when it should update internal state regarding visibility.
  VisibilityChange = 'visibility-change',

  // An update that the widget's parent document visibility has changed.
  // E.g. when a user changes tabs.
  DocumentVisibilityChange = 'document-visibility-change',

  // A response to the MessageToParent.RequestAnalyticsEventId message, giving
  // the iframe the requested analytics event ID.
  AnalyticsEventId = 'analytics-event-id',

  // Ask the iframe to send if it is initialized. If the iframe is not
  // initialized, nothing will get sent. If it is, the iframe will send
  // the initialized message.
  CheckInitialized = 'check-initialized',
}

export interface RequestVisibilityChangeMessage extends BusMessage {
  type: MessageToParentType.RequestVisibilityChange;
  data: {
    requestedVisibility: Visibility;
  };
}

export interface SetPreferredSizeMessage extends BusMessage {
  type: MessageToParentType.SetPreferredSize;
  data: {
    width: number;
    height: number;
  };
}

export interface RequestViewModeMessage {
  type: MessageToParentType.RequestViewMode;
  data: {
    viewMode: ViewMode;
  };
}

export interface SetHeaderHeightMessage extends BusMessage {
  type: MessageToParentType.SetHeaderHeight;
  data: {
    headerHeight: number;
  };
}

export interface RequestAnalyticsEventIdMessage extends BusMessage {
  type: MessageToParentType.RequestAnalyticsEventId;
}

export interface SetUnseenMessagesCountMessage extends BusMessage {
  type: MessageToParentType.SetUnseenMessagesCount;
  data: {
    unseenMessagesCount: number;
  };
}

export interface SetWidgetInitializedMessage extends BusMessage {
  type: MessageToParentType.SetWidgetInitialized;
}

export type MessageToParent =
  | RequestVisibilityChangeMessage
  | RequestViewModeMessage
  | SetPreferredSizeMessage
  | SetHeaderHeightMessage
  | RequestAnalyticsEventIdMessage
  | SetUnseenMessagesCountMessage
  | SetWidgetInitializedMessage;

export interface VisibilityChangeMessage extends BusMessage {
  type: MessageFromParentType.VisibilityChange;
  data: {
    visibility: Visibility;
  };
}

export interface DocumentVisibilityChangeMessage extends BusMessage {
  type: MessageFromParentType.DocumentVisibilityChange;
  data: {
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
    documentVisibility: 'visible' | 'hidden';
  };
}

export interface AnalyticsEventIdMessage extends BusMessage {
  type: MessageFromParentType.AnalyticsEventId;
  data: {
    analyticsEventId: string | null;
  };
}

export type MessageFromParent =
  | VisibilityChangeMessage
  | DocumentVisibilityChangeMessage
  | AnalyticsEventIdMessage;
